import { useEffect, useRef } from 'react';
let observer = null;
const elementsMap = new Map();
const createObserver = () => {
  if (observer) return observer;
  const obs = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      const elementEntry = elementsMap.get(entry.target);
      if (elementEntry) {
        const {
          callback,
          threshold
        } = elementEntry;
        const elementHeight = entry.boundingClientRect.height;
        const viewportHeight = window.innerHeight;
        const isLargerThanViewport = elementHeight > viewportHeight;
        if (isLargerThanViewport) {
          const rect = entry.boundingClientRect;
          const elementBelowViewport = Math.max(0, rect.bottom - viewportHeight);
          const percentageBelowViewport = elementBelowViewport / elementHeight;
          const percentageConsumed = 1 - percentageBelowViewport;
          const hasPassedThreshold = percentageConsumed >= threshold;
          const hasCompletelyPassed = threshold === 1 && rect.bottom <= 0;
          if (hasPassedThreshold || hasCompletelyPassed) {
            callback();
            elementsMap.delete(entry.target);
            obs.unobserve(entry.target);
          }
        } else {
          if (entry.isIntersecting && entry.intersectionRatio >= threshold) {
            callback();
            elementsMap.delete(entry.target);
            obs.unobserve(entry.target);
          }
        }
      }
    });
  }, {
    threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0]
  });
  observer = obs;
  return obs;
};
export const usePublicIntersectionObserver = ({
  onIntersect,
  targetRef,
  threshold = 1.0
}) => {
  const latestOnIntersect = useRef(onIntersect);
  latestOnIntersect.current = onIntersect;
  useEffect(() => {
    const isIntersectionObserverAvailable = typeof window !== 'undefined' && 'IntersectionObserver' in window;
    const currentRef = targetRef === null || targetRef === void 0 ? void 0 : targetRef.current;
    if (!isIntersectionObserverAvailable || !currentRef) {
      return;
    }
    const obs = createObserver();
    elementsMap.set(currentRef, {
      callback: () => latestOnIntersect.current(),
      threshold
    });
    obs.observe(currentRef);
    return () => {
      elementsMap.delete(currentRef);
      obs.unobserve(currentRef);
      if (elementsMap.size === 0) {
        obs.disconnect();
        observer = null;
      }
    };
  }, [targetRef, threshold]);
};